import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  cwListProjects,
  cwCreateWorker,
} from "../../../services/contractWorkers";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Create Worker
// Super-admin only. All fields are plain text inputs except the project, which
// is a select. A super-admin can move the worker to another project later from
// the roster screen.
// ─────────────────────────────────────────────────────────────────────────────

const SHIFT_OPTIONS = ["Morning", "Night"];

const INITIAL = {
  name: "",
  contractCompany: "",
  workProfile: "",
  shift: "Morning",
  projectId: "",
};

const CreateContractWorker = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);

  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(INITIAL);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    cwListProjects()
      .then((res) => setProjects(res.data?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  if (role !== "super-admin") return <Navigate to="/" replace />;

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Worker name is required.";
    if (!form.workProfile.trim())
      errs.workProfile = "Work profile is required.";
    if (!form.projectId) errs.projectId = "Select the project.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("contractCompany", form.contractCompany.trim());
      fd.append("workProfile", form.workProfile.trim());
      fd.append("shift", form.shift);
      fd.append("projectId", form.projectId);
      if (photo) fd.append("photo", photo);

      await cwCreateWorker(fd, form.projectId);
      Swal.fire({
        icon: "success",
        title: "Contract worker created",
        timer: 1400,
        showConfirmButton: false,
      });
      navigate("/contract-workers/roster");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          "Failed to create contract worker.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Create Worker" motherMenu="Contract Workers" />
      <div className="row">
        <div className="col-xl-8 col-lg-10">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Worker Details</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <p className="small text-muted mb-3">
                  Fields marked <span className="text-danger">*</span> are
                  mandatory.
                </p>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Worker Name*</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter worker name"
                        value={form.name}
                        onChange={set("name")}
                      />
                      {errors.name && (
                        <small className="text-danger">{errors.name}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Contract With Company{" "}
                        <span className="text-muted">(optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Contractor / agency name"
                        value={form.contractCompany}
                        onChange={set("contractCompany")}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Project (Appointed At)*
                      </label>
                      <select
                        className="form-control"
                        value={form.projectId}
                        onChange={set("projectId")}
                      >
                        <option value="">Select project…</option>
                        {projects.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.projectName}
                            {p.city ? ` — ${p.city}` : ""}
                          </option>
                        ))}
                      </select>
                      {errors.projectId && (
                        <small className="text-danger">
                          {errors.projectId}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Work Profile*</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Mason, Electrician, Helper"
                        value={form.workProfile}
                        onChange={set("workProfile")}
                      />
                      {errors.workProfile && (
                        <small className="text-danger">
                          {errors.workProfile}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Shift*</label>
                      <select
                        className="form-control"
                        value={form.shift}
                        onChange={set("shift")}
                      >
                        {SHIFT_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Photo <span className="text-muted">(optional)</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={onPhoto}
                      />
                      {photoPreview && (
                        <img
                          src={photoPreview}
                          alt="preview"
                          style={{
                            marginTop: 8,
                            width: 84,
                            height: 84,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={submitting}
                    >
                      {submitting ? "Saving…" : "Create Worker"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/contract-workers/roster")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateContractWorker;
